import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAdminCategories } from "@/hooks/useAdminCategories";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const mobileLinks = [
  { label: "Categorias", to: "/categorias" },
  { label: "Ofertas", to: "/ofertas" },
  { label: "Reviews", to: "/guias" },
  { label: "Guias de Compra", to: "/guias" },
];

const desktopLinks = [
  { label: "Ofertas", to: "/ofertas" },
  { label: "Reviews", to: "/guias" },
  { label: "Guias de Compra", to: "/guias" },
];

const slugify = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { categories } = useAdminCategories();
  const { settings } = useSiteSettings();
  const visibleCategories = categories.filter((category) => category.visible);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (!query) return;
    setMobileMenuOpen(false);
    navigate(`/busca?q=${encodeURIComponent(query)}`);
  };

  const isCategoryMenuActive = pathname.startsWith("/categoria") || pathname.startsWith("/categorias");

  const isLinkActive = (to: string) => {
    if (to === "/guias") {
      return pathname.startsWith("/guias") || pathname.startsWith("/artigo");
    }

    return pathname === to || pathname.startsWith(`${to}/`);
  };

  const menuBackgroundColor = scrolled ? settings.fixedMenuBackgroundColor : settings.initialMenuBackgroundColor;
  const menuTextColor = scrolled ? settings.fixedMenuTextColor : settings.initialMenuTextColor;
  const menuActiveTextColor = scrolled ? settings.fixedMenuActiveTextColor : settings.initialMenuActiveTextColor;

  const getDesktopTextStyle = (active: boolean) => ({
    color: active ? menuActiveTextColor : menuTextColor,
  });

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 shadow-sm"
        style={{ backgroundColor: menuBackgroundColor }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center flex-shrink-0">
              {settings.logoUrl ? (
                <img
                  src={settings.logoUrl}
                  alt={`Logo ${settings.siteName}`}
                  className="h-10 w-auto object-contain"
                />
              ) : (
                <span className="text-xl font-bold" style={{ color: menuActiveTextColor }}>
                  {settings.siteName}
                </span>
              )}
            </Link>

            <div className="hidden md:flex items-center gap-1">
              <div
                className="relative"
                onMouseEnter={() => setMegaMenuOpen(true)}
                onMouseLeave={() => setMegaMenuOpen(false)}
              >
                <button
                  className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-semibold transition-opacity whitespace-nowrap cursor-pointer hover:opacity-80"
                  style={getDesktopTextStyle(isCategoryMenuActive)}
                >
                  <i className="ri-apps-2-line text-base"></i>
                  Categorias
                  <i className={`ri-arrow-down-s-line text-base transition-transform ${megaMenuOpen ? "rotate-180" : ""}`}></i>
                </button>

                {megaMenuOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-[700px] bg-white rounded-2xl shadow-xl border border-gray-100 p-6 grid grid-cols-3 gap-4">
                    {visibleCategories.map((cat) => (
                      <div key={cat.id}>
                        <Link
                          to={`/categoria/${cat.slug}`}
                          className="flex items-center gap-2 font-bold text-gray-800 hover:text-orange-500 transition-colors mb-2 text-sm"
                        >
                          <i className={`${cat.icon} text-orange-500`}></i>
                          {cat.name}
                        </Link>
                        <ul className="space-y-1">
                          {cat.subcategories.slice(0, 4).map((sub) => (
                            <li key={sub}>
                              <Link
                                to={`/categoria/${cat.slug}/${slugify(sub)}`}
                                className="text-xs text-gray-500 hover:text-orange-500 transition-colors flex items-center gap-1 cursor-pointer"
                              >
                                <i className="ri-arrow-right-s-line"></i>
                                {sub}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {desktopLinks.map((item) => {
                const active = isLinkActive(item.to);

                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    className="px-4 py-2 rounded-lg text-sm font-semibold transition-opacity whitespace-nowrap hover:opacity-80"
                    style={getDesktopTextStyle(active)}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>

            <div className="hidden md:flex items-center gap-3">
              <form onSubmit={handleSearch} className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar produtos..."
                  className="w-48 lg:w-64 pl-4 pr-10 py-2 rounded-full text-sm border border-gray-200 bg-white/85 text-gray-800 placeholder-gray-400 transition-all focus:outline-none focus:bg-white"
                />
                <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer">
                  <i className="ri-search-line text-sm" style={{ color: menuTextColor }}></i>
                </button>
              </form>
            </div>

            <button
              className="md:hidden p-2 rounded-lg cursor-pointer"
              style={{ color: menuTextColor }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-2">
            <form onSubmit={handleSearch} className="relative mb-4">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar produtos..."
                className="w-full pl-4 pr-10 py-2.5 rounded-full text-sm border border-gray-200 focus:outline-none focus:border-orange-400"
              />
              <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer">
                <i className="ri-search-line text-gray-400"></i>
              </button>
            </form>
            {mobileLinks.map((item) => {
              const active = item.to === "/categorias" ? isCategoryMenuActive : isLinkActive(item.to);

              return (
                <Link
                  key={item.label}
                  to={item.to}
                  className="block px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors"
                  style={{
                    color: active ? menuActiveTextColor : menuTextColor,
                    backgroundColor: active ? `${menuActiveTextColor}14` : "transparent",
                  }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>
        )}
      </nav>
    </>
  );
}
